/*
 * In-editor voice-leading connector — the gutter mark between two chord chips.
 *
 * Placement (Joni visual spec §2): absolutely positioned in the left gutter /
 * inter-chip channel off the chip's existing `position: relative` shell, so it
 * costs zero layout shift and never reflows the chord run. Vertically centred
 * on the chip's x-height.
 *
 * Encoding (§3): the stroke grammar is reused VERBATIM from the Voice-Leading
 * Lab (voicing-piano-roll.module.css) so the chart and the Lab teach one
 * grammar — held = solid green, step = dotted, leap = dashed.
 */

.connector {
  /* Real <button> reset — the connector inherits keyboard + ARIA for free. */
  position: absolute;
  left: calc(-1 * var(--tuna-spacing-1));
  top: 50%;
  transform: translateY(-50%);

  /* WCAG 2.5.8 target size: the visible glyph is ~10×16px, so pad the hit area
   * to a minimum 24×24px. The inter-chip gutter has no competing tap target. */
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  min-height: 24px;
  padding: 0;

  border: none;
  background: transparent;
  cursor: pointer;
  /* Quietest mark of the set (§4.3): a step below content-secondary weight. */
  opacity: 0.7;
}

/* Discoverability without a tooltip: a faint wash on the gutter zone on
 * hover / focus (§5). The card is the payload; this only signals "clickable". */
.connector:hover,
.connector:focus-visible {
  opacity: 1;
  background-color: var(--tuna-palette-action-hover);
  border-radius: var(--tuna-shape-borderRadius);
}

.connector:focus-visible {
  outline: 2px solid var(--tuna-palette-primary-main);
  outline-offset: 1px;
}

.glyph {
  width: 10px;
  height: 16px;
  overflow: visible;
}

/*
 * WCAG 1.4.1 — the three motion types are distinguishable WITHOUT colour, each
 * carrying a distinct stroke pattern in addition to hue. Strokes copied from
 * the Lab's voicing-piano-roll so the grammar is identical:
 *   held → solid       step → dotted        leap → dashed
 */
.commonToneHeld {
  stroke: var(--tuna-feedback-success, var(--tuna-palette-success-main));
  stroke-width: 2;
}

.step {
  stroke: var(--tuna-content-secondary);
  stroke-width: 1.5;
  stroke-dasharray: 1 2;
  stroke-linecap: round;
}

.leap {
  stroke: var(--tuna-feedback-warning, var(--tuna-palette-warning-main));
  stroke-width: 1.5;
  stroke-dasharray: 3 2;
}

/* Added voice: an open endpoint (a small + drawn off the right end). The voice
 * that "joins" — kept quiet, the rarer case (§3.1). */
.addedGlyph {
  stroke: var(--tuna-content-secondary);
  stroke-width: 1;
  fill: none;
}

/* prefers-reduced-motion: the connector paints instantly — no fade/slide (§8). */
@media (prefers-reduced-motion: reduce) {
  .connector {
    transition: none;
  }
}
